import { orderBy } from 'lodash/fp';
import { useField } from 'formik';
import { useQuery } from '@apollo/client';

import Field from '#/components/forms/Field';
import Listbox, { ListboxProps } from '#/components/controls/Listbox';
import {
  GetVehiclesDocument,
  VehicleDetailsFragmentDoc,
  VehicleDetailsFragment,
} from '#/__generated__/graphql/graphql';
import { isRequiredYupField, useYupRequiredFields } from '#/components/forms/Formik';
import { getFragmentData } from '#/__generated__/graphql';

export const VehicleListbox = <T extends VehicleDetailsFragment, V>({
  additionalItems,
  isLoading,
  ...props
}: VehicleListboxProps<T, V>) => {
  const { data, loading } = useQuery(GetVehiclesDocument);
  const items = orderBy(
    ['cargoVolume.value', 'name'],
    ['asc', 'asc'],
  )([
    ...(data?.vehicles?.edges || [])
      .map(({ node }) => getFragmentData(VehicleDetailsFragmentDoc, node) as T)
      // Hide "special" body type vehicles
      .filter((vehicle) => !!vehicle.bodyType)
      .map((vehicle) => ({
        id: vehicle.id,
        name: vehicle.name,
        // FIXME: no hardcode 'special'
        bodyType: vehicle.bodyType ?? 'Special',
        cargoVolume: vehicle.cargoVolume,
      })),
    ...(additionalItems || []),
  ]) as T[];

  return (
    <Listbox
      {...props}
      displayValue={(item) => item.name}
      getKey={props.getKey!}
      getValue={props.getValue!}
      groupBy={(item) => item.bodyType!}
      isLoading={isLoading ?? loading}
      items={items}
    >
      {(item) => (
        <Listbox.Option key={item.id} value={item}>
          {item.name}
        </Listbox.Option>
      )}
    </Listbox>
  );
};
VehicleListbox.defaultProps = {
  getKey: (id: string) => id,
  getValue: (item: VehicleDetailsFragment) => item.id,
};
export type VehicleListboxProps<T, V> = Omit<
  ListboxProps<T, V>,
  'children' | 'displayValue' | 'items' | 'getKey' | 'getValue' | 'groupBy'
> &
  Partial<Pick<ListboxProps<T, V>, 'getKey' | 'getValue'>> & {
    additionalItems?: T[];
  };

export const VehicleListboxFormik = <T extends VehicleDetailsFragment, V>({
  invalid,
  label,
  name,
  ...props
}: VehicleListboxFormikProps<T, V>) => {
  const [field, meta, helper] = useField<V>(name);
  const requiredFields = useYupRequiredFields();
  const isRequired = isRequiredYupField(requiredFields, name);
  const showError = meta.touched && !!meta.error;

  return (
    <Field name={name}>
      {label && (
        <label className="inline-block mb-1 uppercase">
          {label} {isRequired && <span className="text-red-600">*</span>}
        </label>
      )}
      <VehicleListbox
        name={field.name}
        size="lg"
        value={field.value}
        {...props}
        invalid={invalid ?? showError}
        onChange={helper.setValue}
      />
    </Field>
  );
};
export type VehicleListboxFormikProps<T, V> = VehicleListboxProps<T, V> & {
  label?: string;
  name: string;
};

export default VehicleListbox;
